import request from '../../../../util/APIUtils';
import { PORTAL_API_BASE_URL } from '../../../../constants';
import { CASE_PLAN_REENTRY_EDIT_DETAIL_PERMISSION } from '../constants/reentry-planning.constants';

const DEFAULT_SEARCH_LIMIT = 50;

const CREATE_CASE_PLAN_URL = `${PORTAL_API_BASE_URL}/api/admin/case-plan`;

/**
 * Searches residents eligible for a new case plan (jurisdiction-scoped).
 * @param {{ q?: string, limit?: number }} params
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ items: object[] }>}
 */
export const searchCasePlanResidents = ({ q, limit = DEFAULT_SEARCH_LIMIT } = {}, signal) => {
  const params = new URLSearchParams({
    limit: String(limit),
  });
  const trimmed = (q || '').trim();
  if (trimmed) {
    params.append('q', trimmed);
  }

  return request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plans/residents?${params.toString()}`,
    method: 'GET',
    signal,
  });
};

/**
 * Lists reentry case-plan templates (optional name filter).
 * @param {{ q?: string }} [params]
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ items: object[] }>}
 */
export const fetchCasePlanTemplates = ({ q } = {}, signal) => {
  const params = new URLSearchParams();
  const trimmed = (q || '').trim();
  if (trimmed) {
    params.append('q', trimmed);
  }

  const query = params.toString() ? `?${params.toString()}` : '';

  return request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/templates${query}`,
    method: 'GET',
    signal,
  });
};

/**
 * Searches staff who hold the reentry plan edit permission (editor eligibility).
 * @param {{ q?: string, limit?: number }} params
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ items: object[] }>}
 */
export const searchCasePlanStaffEditors = (
  { q, limit = DEFAULT_SEARCH_LIMIT } = {},
  signal,
) => {
  const params = new URLSearchParams({
    permissions: CASE_PLAN_REENTRY_EDIT_DETAIL_PERMISSION,
    limit: String(limit),
  });
  const trimmed = (q || '').trim();
  if (trimmed) {
    params.append('q', trimmed);
  }

  return request({
    url: `${PORTAL_API_BASE_URL}/api/person/search-staff?${params.toString()}`,
    method: 'GET',
    signal,
  });
};

/**
 * Creates one or more case plans (bulk). Requires Janus token on the request
 * (loaded via withRolePermission when Create Plan mounts).
 * @param {{ planType: string, learnerIds: number[], editorIds?: number[], templateId?: string }} payload
 * @returns {Promise<{ results: Array<{ learnerId: number, planId?: string, status: string, reason?: string }> }>}
 */
export const createCasePlans = (payload) =>
  request({
    url: CREATE_CASE_PLAN_URL,
    method: 'POST',
    body: JSON.stringify(payload),
  });
